import { css } from 'aphrodite';
import * as React from 'react';
import type { Scenario } from '../../../voice-agent/types/scenarios';
import { Button } from '../../../../web/components-v2/Button';
import { styleSheet } from './styles';

/**
 * Props for the ScenarioRow component
 */
export interface ScenarioRowProps {
	/** The scenario to display */
	scenario: Scenario;
	/** Callback when the edit button is clicked */
	onEdit: () => void;
	/** Callback when the delete button is clicked */
	onDelete: () => void;
}

/**
 * ScenarioRow component
 *
 * Renders a single scenario row within a skill area.
 * The scenario row displays the name, description, active status badge,
 * and action buttons (Edit, Delete).
 *
 * This is the simplest row component in the hierarchy - no expansion capability.
 */
const ScenarioRowComponent: React.FC<ScenarioRowProps> = ({ scenario, onEdit, onDelete }) => {
	return (
		<div className={css(styleSheet.row, styleSheet.scenarioRow)}>
			<div className={css(styleSheet.bulletIndicator)}>•</div>
			<div className={css(styleSheet.rowContent)}>
				<div className={css(styleSheet.rowName)}>{scenario.name}</div>
				{scenario.description && <div className={css(styleSheet.rowDescription)}>{scenario.description}</div>}
			</div>
			<div
				className={css(styleSheet.statusBadge, scenario.active ? styleSheet.statusActive : styleSheet.statusInactive)}
			>
				{scenario.active ? 'Active' : 'Inactive'}
			</div>
			<div className={css(styleSheet.actions)}>
				<Button variant='secondary' size='sm' onClick={onEdit} aria-label={`Edit ${scenario.name}`}>
					Edit
				</Button>
				<Button variant='destructive-secondary' size='sm' onClick={onDelete} aria-label={`Delete ${scenario.name}`}>
					Delete
				</Button>
			</div>
		</div>
	);
};

/**
 * Memoized ScenarioRow component for performance optimization.
 * Re-renders only when the scenario or callbacks change.
 */
export const ScenarioRow = React.memo(ScenarioRowComponent);
